/** 编码缓存有效期：同图 5 分钟内不重复编码 */
export const ENCODE_CACHE_TTL_MS = 5 * 60 * 1000;

/** Fabric 对象标记（图组与图层） */
export const SPLIT_GROUP_TAG = '_isSplitGroup';
export const SPLIT_LAYER_FILE_TAG = '_splitLayerFile';
export const SPLIT_LAYER_NAME_TAG = '_splitLayerName';
/** Fabric 对象标记：拆分图层服务端稳定 layerId（修补/回滚提交用），刷新后据此恢复 */
export const SPLIT_LAYER_ID_TAG = '_splitLayerId';
export const SPLIT_TASK_ID_TAG = '_splitTaskId';
/** Fabric 对象标记：拆分会话 clientId / resultRevision（图组），刷新后重建详情时恢复修补/回滚/继续拆分所需会话（需求 39） */
export const SPLIT_CLIENT_ID_TAG = '_splitClientId';
export const SPLIT_RESULT_REVISION_TAG = '_splitResultRevision';
/** Fabric 对象标记：拆图结果最新 manifest URL（图组），修补/回滚/继续拆分前据此拉取服务端最新 revision（需求 59） */
export const SPLIT_RESULT_MANIFEST_URL_TAG = '_splitResultManifestUrl';
/** Fabric 对象标记：拆分会话所属作品 id（图组），刷新后重建详情时恢复，排序/删除/修补按此提交（需求 123） */
export const SPLIT_ARTWORK_ID_TAG = '_splitArtworkId';
/** Fabric 对象标记：修补历史版本（图组子对象），刷新后重建详情恢复「↩ 回滚修补」入口（需求 66） */
export const SPLIT_REPAIR_VERS_TAG = '_splitRepairVers';
/** Fabric 对象标记：拆分扫描态临时对象（副本/条纹遮罩/扫描线），刷新后据标签清理 */
export const SPLIT_SCAN_TAG = '_isSplitScanObject';
/** Fabric 对象标记：复制落地图组的源会话身份（源 clientId），跨画板「定位/判重」据此命中副本，
 * 刷新后仍有效（图组随画布持久化）。落地图组的 SPLIT_CLIENT_ID_TAG 是副本新会话，不可用于判源 */
export const SPLIT_SOURCE_CLIENT_ID_TAG = '_splitSourceClientId';
/** Fabric 对象标记：复制落地图组的源拆分版本 id（sourceVersionId），与源 clientId 配套用于对账 */
export const SPLIT_SOURCE_VERSION_ID_TAG = '_splitSourceVersionId';
/** Fabric 对象标记：图组背景当前的补图引擎（manifest.background.engine：LAMA/KLEIN）。
 * Klein 与 LaMa 共用同一 imageObjectKey 覆盖写时 URL 不变，纯 URL 比对判 unchanged——
 * 引擎标记是唯一可靠的升级判据（0902 反馈「画布停留 lama」结构性盲区的解法） */
export const SPLIT_BG_ENGINE_TAG = '_splitBgEngine';

/** 修补绿光派生覆盖层 key：与主拆分 'main' 及派生命令 commandId 并列，
 * 修补不再占用 'main' 单例字段（避免并发主拆分时互相覆盖/提前清锁，0901 反馈） */
export const SPLIT_REPAIR_SCAN_KEY = 'repair';

/** 图组左上角「已拆分」标签文案（需求 56：常驻角标，放左上角） */
export const SPLIT_BADGE_TEXT = '已拆分';

/** 被扫图层左上角状态角标文案（0910 需求：拆分族任务进行中提示），key 与 store.splitStatusTaskKind 对应 */
export const SPLIT_TASK_STATUS_LABELS = {
  split: '全能拆分-拆分中',
  continue: '全能拆分-继续拆分中',
  repair: '全能拆分-修补中',
} as const;

/** 拆分操作算力消耗（服务端已完成计费，前端仅做 UI 展示；需求 37/103） */
export const SPLIT_CONSUME_POWER = 50;   // 手动拆分 / 自动拆分
export const EXTRACT_CONSUME_POWER = 10; // 提取元素
export const RE_SPLIT_POWER = 10;        // 继续拆分（入口与执行弹窗必须一致）
export const REPAIR_POWER = 10;          // 修补图像

/** 画廊拆分素材拖拽落地：图组显示宽度上限（未超过上限时按素材真实像素 1:1 落地，等比缩放） */
export const GALLERY_SPLIT_DROP_MAX_WIDTH = 1200;

/**
 * 拆分进度轮播文案（对齐 game-ui-splitter demo 的 scan-tip 文案）
 * NOTE: 该包 barrel 未导出这两个常量，面板侧扫描动效直接复用，故本地复制一份
 */
export const SPLITTING_TIPS = [
  '🔍 AI 正在理解每个选区的精确轮廓…',
  '✂️ 逐元素精细抠图，边缘做发丝级处理…',
  '🧠 推理元素之间的层叠与遮挡关系…',
  '🎨 被压住的区域正在由 AI 补全重绘…',
  '🖼 背景修复中：挖走元素、织补空洞…',
  '📝 识别文字内容与样式，转成可编辑文本…',
  '💡 完成后双击图组，可进入详情继续编辑',
  '💡 详情里右击图层：可继续拆分、修补甚至扩图',
] as const;

export const SPLITTING_TIP_INTERVAL_MS = 2600;

/** 拆分副本与原图右侧间距（对齐 demo spawnSplitCopy 的 gap=50） */
export const SPLIT_COPY_GAP = 50;

/** 拆分副本滑出动画时长（对齐 demo `transition: left .5s ease`） */
export const COPY_SLIDE_DURATION_MS = 500;
/** 视口适配平滑过渡时长（对齐 demo world 容器 `transform .45s ease`） */
export const VIEWPORT_FIT_DURATION_MS = 450;
/** 视口适配延迟：等副本滑出起手后再开始（对齐 demo 延迟到 UI 稳定） */
export const VIEWPORT_FIT_DELAY_MS = 100;

/** 画布上方居中 toast：停留时长（对齐 demo toastOut 2.2s 延迟） */
export const CANVAS_TOAST_VISIBLE_MS = 2200;
/** 画布上方居中 toast：淡出时长（对齐 demo toastOut 0.4s 动画） */
export const CANVAS_TOAST_FADE_MS = 400;
/** 拆分完成绿色闪光：延迟出现（对齐 demo flashSplitDone 350ms） */
export const FLASH_DELAY_MS = 350;
/** 拆分完成绿色闪光：扩散动画时长（对齐 demo ciFlash 1.1s） */
export const FLASH_DURATION_MS = 1100;
